import api from 'api';
import { useState, useEffect } from 'react';
import { ReactComponent as ThreeLines } from 'assets/icons/B2B/dehaze.svg';
import Popover from '@mui/material/Popover';
import RightSidebar from './RightSidebar';
import Button from '../Button';
import FooterButton from '../FooterButton';
import { ReactComponent as CrossSimpleIcon } from 'assets/icons/cross-simple.svg';
import { Notification } from 'api/models/response/Notifications';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../../Dashboard.styled';

type MenuForDropdownsProps = {
    clientView: boolean;
    clientFlow: boolean;
    onboarding: boolean;
    mobile: boolean;
};

const MenuForMobileDropdowns = ({
    clientView,
    clientFlow,
    onboarding,
    mobile,
}: MenuForDropdownsProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const [showClearAllNotificationsButton, setShowClearAllNotificationsButton] =
        useState<boolean>(false);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        if (open) {
            setAnchorEl(null);
        }
    };

    useEffect(() => {
        const handleStorageChange = () => {
            const storedNotificationValue = localStorage.getItem('clearAllNotificationsButton');
            setShowClearAllNotificationsButton(storedNotificationValue === 'show');
        };

        window.addEventListener('storageValueChanged', handleStorageChange);
        handleStorageChange();
        return () => {
            window.removeEventListener('storageValueChanged', handleStorageChange);
        };
    }, []);

    const deleteNotification = async (notification: Notification) => {
        try {
            await api.notification.deleteNotification(notification.id);
        } catch (e) {
            console.error(e);
        }
    };

    const deleteAllNotifications = async () => {
        try {
            const response = await api.notification.get();
            const deletePromises = response.data.map((notification: Notification) =>
                deleteNotification(notification)
            );
            await Promise.all(deletePromises);
            localStorage.setItem('reloadNotifications', 'yes');
            dispatchEvent(new Event('storageValueChanged'));
            setShowClearAllNotificationsButton(false);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <FooterButton
                color="purple"
                isActive={!!anchorEl}
                onClick={(event) => {
                    handleClick(event);
                }}
            >
                <ThreeLines style={{ transform: 'scale(1.5)' }} />
                {t('menu', { lng: language })}
            </FooterButton>
            <Popover
                id={id}
                open={open}
                anchorPosition={{ top: 0, left: 0 }}
                onClose={handleClose}
                marginThreshold={0}
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: '1400px',
                            minHeight: '100%',
                            width: '100%',
                            overflow: 'visible',
                            boxShadow: '0px 4px 12px rgba(50, 29, 29, 0.09)',
                        },
                    },
                }}
            >
                <S.MainLayoutForDropdowns mobile={mobile}>
                    <S.Page mobile={mobile}>
                        <S.MainContainer reducedWidth={clientView || clientFlow} mobile={mobile}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent:
                                        clientFlow && showClearAllNotificationsButton
                                            ? 'space-between'
                                            : 'flex-end',
                                    marginTop: '10px',
                                }}
                            >
                                {clientFlow && showClearAllNotificationsButton && (
                                    <Button background="purple" onClick={deleteAllNotifications}>
                                        {t('clearAllNotifications', { lng: language })}
                                    </Button>
                                )}
                                <Button
                                    onClick={handleClose}
                                    background="red"
                                    style={{ width: '100px', fontWeight: '700' }}
                                >
                                    <CrossSimpleIcon style={{ fill: '#282828' }} />
                                    {t('close', { lng: language })}
                                </Button>
                            </div>

                            <RightSidebar
                                clientFlow={clientFlow}
                                onboarding={onboarding}
                                mobile={mobile}
                                closeMenu={handleClose}
                            />
                        </S.MainContainer>
                    </S.Page>
                </S.MainLayoutForDropdowns>
            </Popover>
        </>
    );
};

export default MenuForMobileDropdowns;
